import {
  useCreateDocumentMutation,
  useCreateDocumentVersionMutation,
  usePatchDocumentMutation,
} from "@hooks";
import { Document, DocumentVersion } from "@models";

type CreateUserUploadDocumentOptions = {
  folderId?: string | null;
};

/**
 * Upload a file as a document, creates the first version as well
 */
export const useCreateUserUploadDocument = (
  orgId: string,
  deviceId?: string,
  options: CreateUserUploadDocumentOptions = {}
) => {
  const createDocumentMutation = useCreateDocumentMutation();
  const createDocumentVersionMutation = useCreateDocumentVersionMutation(
    orgId,
    deviceId ?? ""
  );
  const patchDocumentMutation = usePatchDocumentMutation();
  const targetFolderId = options.folderId ?? null;

  const assignDocumentToTargetFolder = async (document: Document) => {
    if (!targetFolderId || document.folderId === targetFolderId) {
      return document;
    }

    return patchDocumentMutation.mutateAsync({
      orgId,
      docId: document.id,
      deviceId: deviceId ?? "",
      queryDeviceIds: deviceId ? [deviceId] : [],
      data: {
        folderId: targetFolderId,
      },
    });
  };

  /**
   * Create a new document and version from a file
   * @param file - The file to upload
   * @param createRecord.linkedToTemplateKey - If you want this document to be a record related to another document, provide the templateKey
   * @returns The created document and version
   */
  const uploadFileAsDocument = async (
    file: File,
    createRecord?: {
      linkedToTemplateKey: string;
    }
  ): Promise<Document> => {
    // TODO: Allow this to be org wide
    const document = await createDocumentMutation.mutateAsync({
      orgId,
      body: {
        ...(deviceId ? { deviceId } : {}),
        title: file.name,
        userUpload: true,
        ...(createRecord?.linkedToTemplateKey
          ? { templateKey: createRecord.linkedToTemplateKey }
          : {}),
      },
    });

    const documentVersion = await createDocumentVersionMutation.mutateAsync({
      docId: document.id,
      file,
    });
    const assignedDocument = await assignDocumentToTargetFolder(document);

    return {
      ...assignedDocument,
      versions: [documentVersion],
    };
  };

  return { uploadFileAsDocument };
};

export const useUserUploadTemplate = (
  orgId: string,
  deviceId: string,
  options?: CreateUserUploadDocumentOptions
) => useCreateUserUploadDocument(orgId, deviceId, options);

/**
 * Create a new version of an existing user-upload document.
 * Creates a draft upload version.
 */
export const useCreateNewUserUploadDocumentVersion = (
  orgId: string,
  deviceId?: string
) => {
  const createDocumentVersionMutation = useCreateDocumentVersionMutation(
    orgId,
    deviceId ?? ""
  );

  const createNewVersion = async (
    docId: string,
    file: File
  ): Promise<DocumentVersion> => {
    return createDocumentVersionMutation.mutateAsync({
      docId,
      file,
    });
  };

  return {
    createNewVersion,
    isPending: createDocumentVersionMutation.isPending,
  };
};
